import { CSSProperties, useMemo } from "react";
import { useToasts } from "./useToasts";

const STACKED_TOAST_HEIGHT = "5.3125rem"; // border + padding + title + gap + message
const STACKED_TOAST_PEEK_HEIGHT = "1rem";

/**
 * Hook for calculating position styles for individual toasts
 * @param index - The index of the toast in the stack
 * @returns CSS properties for positioning the toast
 */
export const useToastPositionStyles = (
  index: number,
): CSSProperties | undefined => {
  const {
    toasts,
    toastHeights,
    getToastStackHeight,
    toastsBeforeStack,
    extraToastsVisible,
  } = useToasts();
  const toastCount = toasts.length;
  const toasterHeight = getToastStackHeight(index);

  return useMemo(() => {
    if (index === toastCount - 1) {
      return {
        top: 0,
        zIndex: 3,
      };
    } else if (
      // all toasts when show more is clicked
      extraToastsVisible ||
      // or visible toasts when stacked
      (!extraToastsVisible &&
        (toastCount <= toastsBeforeStack ||
          (toastCount > toastsBeforeStack &&
            index >= toastCount - toastsBeforeStack)))
    ) {
      return {
        top: toasterHeight,
      };

      // stacked toasts
    } else {
      if (toastCount > toastsBeforeStack) {
        // first stack
        if (index === toastCount - toastsBeforeStack - 1) {
          return {
            top: `calc(${toasterHeight || 0}px - ${STACKED_TOAST_HEIGHT} +
              ${STACKED_TOAST_PEEK_HEIGHT})`,
            zIndex: 1,
          };
          // second stack and hidden toasts
        } else if (index <= toastCount - toastsBeforeStack - 2) {
          return {
            top: `calc(${toasterHeight || 0}px -
              ${STACKED_TOAST_HEIGHT} 
             + ${STACKED_TOAST_PEEK_HEIGHT} * 2)`,
            zIndex: 0,
          };
        }
      }
    }
    return undefined;
    // only run when toastHeights changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toastHeights]);
};
